import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import {
  BACKTESTING_EXTRA_DETAILS_PATH,
  MAX_BACKTEST_DRAWER_EXTENSION_SCREEN,
  MOBILE_SEARCH,
} from "common/constants";
import React, { useContext, useMemo } from "react";
import GeneralTab from "features/bots/BotDrawer/GeneralTab";
import TextWithIcon from "common/Text/TextWithIcon";
import BacktestingStatistics from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/BacktestingStatistics";
import useMedia from "utils/hooks/useMedia";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useBottomNavigator, useDrawerDelayedUnmount } from "utils/hooks/misc";
import useQuery from "utils/hooks/useQuery";
import {
  ACCOUNT_ID_QUERY_PARAM,
  DRAWER_BACKTESTING_QUERY_PARAM,
  GROUP_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import {
  getBacktetingExtraDetailsRoute,
  getRouteWithoutPath,
} from "utils/routes";
import GeneralTabStatistics from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/GeneralTabStatistics";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import DrawerTradesPage from "features/trades/common/DrawerTradesPage";
import AnimatedInProgressIcon from "common/Animations/AnimatedInProgressIcon";
import {
  BacktestingStatus,
  BotType,
  FilterType,
  LanguageNameSpaces,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import BotDetailsProvider from "@shared/features/bots/BotDetailsProvider";
import InProgressMessage from "features/bots/BotForm/Backtesting/common/InProgressMessage";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { DEFAULT_DRAWER_WIDTH } from "common/Drawers/Drawer";
import Disclaimer from "features/bots/BotForm/Backtesting/common/Disclaimer";
import BacktestDetailsProvider from "@shared/features/bots/BacktestDetailsProvider";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import { ICON_TYPES } from "@shared/common/constants";
import { isSymbol } from "@shared/types/utils";
import { BacktestingExtraDetailsTabs } from "@shared/features/bots/types.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { skipToken } from "@reduxjs/toolkit/query";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import BacktestDrawerTitle from "features/bots/BotForm/Backtesting/NewLook/BacktestDrawerTitle";
import BacktestPerformance from "features/bots/BotForm/Backtesting/NewLook/BacktestPerformance";
import NewLookBacktestStatistics from "features/bots/BotForm/Backtesting/NewLook/BacktestStatistics";

const BacktestingDrawer = () => {
  const query = useQuery();
  const match = useRouteMatch(BACKTESTING_EXTRA_DETAILS_PATH);
  const { botId: botIdStr } = (match?.params || {}) as { botId?: string };
  const botId = botIdStr ? parseInt(botIdStr) : undefined;
  const backtestingIdStr = query.get(DRAWER_BACKTESTING_QUERY_PARAM);
  const backtestingId = backtestingIdStr
    ? parseInt(backtestingIdStr)
    : undefined;

  const { id: drawerBotId, isUnmounting: botIsUnmounting } =
    useDrawerDelayedUnmount(botId);
  const { id: drawerBacktestingId, isUnmounting: backtestingIsUnmounting } =
    useDrawerDelayedUnmount(backtestingId);

  return (
    <BotDetailsProvider
      botId={drawerBotId}
      isBacktesting={true}
      isChannelConfiguration={false}
    >
      <BacktestDetailsProvider
        backtestingId={drawerBacktestingId}
        backtestingBotId={drawerBotId}
      >
        <BacktestingDrawerContent
          botId={drawerBotId}
          isOpen={
            !!botId &&
            !botIsUnmounting &&
            !!drawerBacktestingId &&
            !backtestingIsUnmounting
          }
        />
      </BacktestDetailsProvider>
    </BotDetailsProvider>
  );
};

export default BacktestingDrawer;

interface BacktestingDrawerContentProps {
  botId?: number;
  isOpen?: boolean;
}

const BacktestingDrawerContent = ({
  botId,
  isOpen,
}: BacktestingDrawerContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  const {
    inProgress,
    botData,
    liveBacktestingData,
    liveBarChartData,
    backtestingJobResults,
    openTradesData,
    closedTradesData,
    loading,
  } = useContext(BacktestDetailsContext)!;
  const isSmallScreen = useMedia(
    [`(max-width: ${MAX_BACKTEST_DRAWER_EXTENSION_SCREEN})`],
    [true],
    false
  );
  const isCardView = useMedia(
    [`(max-width: ${CARDS_VIEW_WIDTH}px)`],
    [true],
    false
  );
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const history = useHistory();
  const { getUpdatedPath } = useHistoryWithQuery();
  const isSignalsBot = botData?.bot_type === BotType.SIGNAL;
  const baseGetTradesArgs = useMemo(
    () => (botId ? { dca_bot_id: botId, is_backtesting: true } : skipToken),
    [botId]
  );

  const inProgressIcon = inProgress && (
    <IconBg>
      <AnimatedInProgressIcon style={{ width: 22, height: 22 }} />
    </IconBg>
  );
  const inProgressMessage = inProgress && <InProgressMessage />;

  let titleProps = {};
  if (isSignalsBot) titleProps = { iconLink: botData?.sub_group_image };
  else if (botData?.symbols && !isSymbol(botData?.symbols)) {
    titleProps = {
      iconType: ICON_TYPES.COIN,
      coinOrExchangeName: botData?.symbols[0],
    };
  }
  const classicTitle = botData?.name ? (
    <TextWithIcon
      text={
        <Flex>
          {botData?.name}
          {inProgressIcon}
        </Flex>
      }
      style={{ width: "100%" }}
      justify="center"
      iconStyle={{ borderRadius: 4, marginRight: 8 }}
      {...titleProps}
    />
  ) : undefined;

  // New look: the header reads left to right like the bot drawer's.
  const title = isNewLook ? (
    botData || liveBacktestingData ? (
      <BacktestDrawerTitle run={liveBacktestingData} botData={botData} />
    ) : undefined
  ) : (
    classicTitle
  );

  const extraComponent = (
    <Disclaimer
      style={{ marginTop: isBottomNavigator ? 8 : isNewLook ? 12 : 16 }}
    />
  );

  const tabToDisplayName = {
    [BacktestingExtraDetailsTabs.GENERAL]: t("backtesting.tabs.general"),
    [BacktestingExtraDetailsTabs.TRADES]: t("backtesting.tabs.trades"),
    [BacktestingExtraDetailsTabs.CONFIGURATION]: t(
      "backtesting.tabs.configuration"
    ),
  };

  const {
    tab,
    tabDisplayName,
    onChange: onTabChange,
  } = useTabsWithUrl<BacktestingExtraDetailsTabs>({
    tabToDisplayName,
    tabs: Object.values(BacktestingExtraDetailsTabs),
    path: BACKTESTING_EXTRA_DETAILS_PATH,
    getPathFromTab: (tab, location) => {
      if (botId)
        return getBacktetingExtraDetailsRoute(
          location,
          botId.toString() || "",
          tab
        );
    },
    skipUrlInitialization: !isOpen,
    defaultTab: BacktestingExtraDetailsTabs.GENERAL,
  });

  const generalStatistics = (
    <GeneralTabStatistics
      backtestingRun={liveBacktestingData}
      botData={botData}
      liveBarChartData={liveBarChartData}
      backtestingJobResults={backtestingJobResults}
    />
  );

  const padding = isMobile ? 16 : "0 24px";
  const configuration = botId && botData && (
    <GeneralTab
      botData={botData}
      botId={botId}
      backtesting={liveBacktestingData}
      isBacktesting={true}
      style={{ padding }}
    />
  );

  const statistics = (
    <BacktestingStatistics
      isExtension={!isSmallScreen}
      inProgress={inProgress}
      loading={loading}
    />
  );

  // New look: the result leads (net PnL over its chart), the statistics
  // follow; no spinner over an empty drawer, the parts load as skeletons.
  const newLookGeneralTabContent = (
    <>
      <NewLookGeneral $padding={padding}>
        {isSmallScreen && inProgressMessage}
        <BacktestPerformance
          backtestingRun={liveBacktestingData}
          botData={botData}
          liveBarChartData={liveBarChartData}
          backtestingJobResults={backtestingJobResults}
          loading={loading}
          showDaily={true}
          compact={isMobile}
          chartHeight={isMobile ? 168 : 200}
        />
        {isSmallScreen && <NewLookBacktestStatistics loading={loading} />}
      </NewLookGeneral>
      {!isSmallScreen && configuration}
    </>
  );

  const generalTabContent = isNewLook ? (
    newLookGeneralTabContent
  ) : (
    <LoadingSpinnerOverlay
      isLoading={loading}
      spinnerSize="large"
      hideContentWhileLoading={true}
    >
      <div style={{ padding, marginBottom: isBottomNavigator ? 0 : 24 }}>
        {isSmallScreen && inProgressMessage}
        {generalStatistics}
      </div>
      {isSmallScreen ? statistics : configuration}
    </LoadingSpinnerOverlay>
  );

  let tabToComponent: {
    [key in BacktestingExtraDetailsTabs]?: React.JSX.Element;
  } = {
    [tabToDisplayName[BacktestingExtraDetailsTabs.GENERAL]]: generalTabContent,
  };

  if (isSmallScreen) {
    tabToComponent[
      tabToDisplayName[BacktestingExtraDetailsTabs.CONFIGURATION]
    ] = configuration;
  }

  tabToComponent[tabToDisplayName[BacktestingExtraDetailsTabs.TRADES]] = (
    <div style={{ padding: isBottomNavigator ? 0 : padding, height: "100%" }}>
      <DrawerTradesPage
        baseGetTradesArgs={baseGetTradesArgs}
        isDrawerOpen={isOpen}
        isCardView={isCardView}
        openTradesData={openTradesData}
        closedTradesData={closedTradesData}
        disableTradeClick={
          liveBacktestingData?.status !== BacktestingStatus.COMPLETED
        }
        hideFilterTypes={TRADES_HIDE_FILTER_TYPES}
        tabKey={tabToDisplayName[BacktestingExtraDetailsTabs.TRADES]}
      />
    </div>
  );

  const closeDrawer = () => {
    const updatedPath = getUpdatedPath(
      undefined,
      [
        DRAWER_BACKTESTING_QUERY_PARAM,
        ACCOUNT_ID_QUERY_PARAM,
        GROUP_ID_QUERY_PARAM,
      ],
      getRouteWithoutPath(location.pathname, BACKTESTING_EXTRA_DETAILS_PATH)
    );
    history.push(updatedPath);
  };

  return (
    <ExtraDetailsDrawer
      visible={!!isOpen}
      title={title}
      extraComponent={extraComponent}
      tabsToComponents={tabToComponent}
      tab={tabDisplayName}
      onTabChange={onTabChange}
      extension={isSmallScreen ? undefined : statistics}
      hideExtensionBorder={true}
      extensionWidth={475}
      mainContentWidth={FULL_SIZE_TRADES_TABLE}
      minWidth={
        isSmallScreen
          ? isCardView
            ? DEFAULT_DRAWER_WIDTH
            : CARDS_VIEW_WIDTH
          : FULL_SIZE_TRADES_TABLE
      }
      path={BACKTESTING_EXTRA_DETAILS_PATH}
      closeDrawer={closeDrawer}
      innerContentWrapperStyle={{ padding: 0 }}
      flatListTabs={[tabToDisplayName[BacktestingExtraDetailsTabs.TRADES]]}
      showMobileSearch={tab === BacktestingExtraDetailsTabs.TRADES}
    />
  );
};

const TRADES_HIDE_FILTER_TYPES = [
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.SOURCE_TYPE,
  FilterType.SOURCE_NAME,
];
const CARDS_VIEW_WIDTH = 775;
const FULL_SIZE_TRADES_TABLE = 900;

const IconBg = styled.div`
  background: ${(p) => p.theme.colors.lightBlue};
  border-radius: 8px;
  margin-left: 12px;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NewLookGeneral = styled.div<{ $padding: number | string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${(p) =>
    typeof p.$padding === "number" ? `${p.$padding}px` : p.$padding};
  padding-bottom: 24px;
`;
